/**
 * A party's bank accounts for payments: the list, adding one, signing, rejecting, revoking,
 * editing, deactivating and the history. Shared by the partner detail and our company's settings.
 */
import { useState } from 'react';
import { Button, EmptyState, Loader, Toggle } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useBankAccountPermissions } from '../hooks/use-bank-account-permissions';
import { usePartyBankAccounts, type PartyBankAccount } from '../hooks/use-party-bank-accounts';
import { bankAccountActions } from '../utils/bank-account-actions';
import { AddBankAccountModal } from './AddBankAccountModal';
import { BankAccountHistoryModal } from './BankAccountHistoryModal';
import { DeactivateBankAccountModal } from './DeactivateBankAccountModal';
import { EditBankAccountModal } from './EditBankAccountModal';
import { PartyBankAccountRow, type BankAccountRowAction } from './PartyBankAccountRow';
import { SignBankAccountModal } from './SignBankAccountModal';
import { TerminalBankAccountModal } from './TerminalBankAccountModal';

type OpenDialog =
  { kind: 'add' } | { kind: BankAccountRowAction; account: PartyBankAccount } | null;

interface PartyBankAccountsPanelProps {
  partyId: string;
  /** The party's name, compared with the name the VAT register gives before the second signature. */
  partyName: string | null;
}

export function PartyBankAccountsPanel({ partyId, partyName }: PartyBankAccountsPanelProps) {
  const t = useFeatureTranslation('partyBankAccounts');
  const permissions = useBankAccountPermissions();
  const [showAll, setShowAll] = useState(false);
  const { accounts, hasMore, loadMore, loading, loadingMore, error } = usePartyBankAccounts(
    partyId,
    showAll
  );
  const showLoader = useDelayedLoading(loading);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const close = () => setDialog(null);

  let body;
  if (loading) {
    body = showLoader ? (
      <div className='flex justify-center py-lg'>
        <Loader size='sm' />
      </div>
    ) : null;
  } else if (error && accounts.length === 0) {
    body = (
      <p role='alert' className='text-sm text-error-text'>
        {t('loadError')}
      </p>
    );
  } else if (accounts.length === 0) {
    body = <EmptyState size='sm' icon='wallet' message={t('empty')} testId='bank-accounts-empty' />;
  } else {
    body = (
      <ul className='flex flex-col gap-xs' data-testid='bank-accounts-list'>
        {accounts.map(account => (
          <PartyBankAccountRow
            key={account.id}
            account={account}
            actions={bankAccountActions(account, permissions)}
            onAction={(kind, target) => setDialog({ kind, account: target })}
          />
        ))}
      </ul>
    );
  }

  return (
    <div className='flex flex-col gap-md'>
      <p className='text-sm text-foreground-muted'>{t('description')}</p>
      <div className='flex flex-wrap items-center justify-between gap-sm'>
        <Toggle
          size='sm'
          labelText={t('showAll')}
          checked={showAll}
          onChange={setShowAll}
          testId='bank-accounts-show-all'
        />
        {permissions.canManage && (
          <Button
            variant='default'
            outline='default'
            size='sm'
            icon='plus'
            testId='bank-account-add'
            onClick={() => setDialog({ kind: 'add' })}
          >
            {t('add')}
          </Button>
        )}
      </div>

      {body}

      {hasMore && (
        <Button
          variant='default'
          outline='default'
          size='sm'
          className='self-center'
          disabled={loadingMore}
          onClick={() => void loadMore()}
        >
          {t('loadMore')}
        </Button>
      )}

      {dialog?.kind === 'add' && <AddBankAccountModal partyId={partyId} onClose={close} />}
      {dialog?.kind === 'sign' && (
        <SignBankAccountModal account={dialog.account} partyName={partyName} onClose={close} />
      )}
      {dialog?.kind === 'edit' && <EditBankAccountModal account={dialog.account} onClose={close} />}
      {dialog?.kind === 'history' && (
        <BankAccountHistoryModal account={dialog.account} partyName={partyName} onClose={close} />
      )}
      {(dialog?.kind === 'reject' || dialog?.kind === 'revoke') && (
        <TerminalBankAccountModal account={dialog.account} action={dialog.kind} onClose={close} />
      )}
      {dialog?.kind === 'deactivate' && (
        <DeactivateBankAccountModal account={dialog.account} onClose={close} />
      )}
    </div>
  );
}
